import React, { useState } from 'react';
import { useLazyCommand, useEvent } from '@soap/modules';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

interface AddTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tagTypeKey: string;
  onTagAdded?: () => void;
}

export function AddTagDialog({ open, onOpenChange, tagTypeKey, onTagAdded }: AddTagDialogProps) {
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [sendCommand] = useLazyCommand('C120v1_AddTag');

  useEvent({
    eventName: 'E105v1_TagAdded',
    onEventReceived: () => {
      if (submitting) {
        setSubmitting(false);
        setTagName('');
        setTagDescription('');
        onOpenChange(false);
        onTagAdded?.();
      }
    }
  }, [submitting, onOpenChange, onTagAdded]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tagName.trim()) return;

    setSubmitting(true);
    sendCommand({
      $type: 'C120v1_AddTag',
      C120_TagName: tagName.trim(),
      C120_TagDescription: tagDescription.trim(),
      C120_TagTypeKey: tagTypeKey
    });
  };

  const handleClose = () => {
    if (!submitting) {
      setTagName('');
      setTagDescription('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Tag</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tagName">Tag Name</Label>
            <Input
              id="tagName"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              required
              placeholder="Enter tag name"
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tagDescription">Description (Optional)</Label>
            <Input
              id="tagDescription"
              value={tagDescription}
              onChange={(e) => setTagDescription(e.target.value)}
              placeholder="Enter description"
              disabled={submitting}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!tagName.trim() || submitting}>
              {submitting ? 'Adding...' : 'Add Tag'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

